import {
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { escapeUnknownRawTags } from '@/lib/markdown/rehypeEscapeUnknownRawTags';
import { cx } from '@/utils/cx';

import { InlineToolbarSkeleton } from '../memo/InlineToolbarSkeleton';
import { PlateNoteEditor } from '../memo/PlateNoteEditor';
import { useInlineFieldEdit } from './useInlineFieldEdit';

interface TaskDescriptionEditorProps {
  content: string;
  orgId: number;
  canEdit: boolean;
  onSave: (content: string) => Promise<void>;
}

// Matches the note record slideout's body text (NoteCard) so a task's
// description reads identically to a note's.
const READ_CLASS =
  'text-sm text-secondary break-words max-w-none [&_*]:text-inherit';

// The same reserved edit frame the note record slideout draws around its body
// (see NoteCard's `useInlineEdit` box): padding + rounded border geometry is
// IDENTICAL in read and edit — only the border colour changes (transparent →
// visible) on click-in, so the text never shifts a pixel. `-mx-3` cancels the
// `p-3` horizontally so the text sits flush with the section's left edge and
// the edit-only border sits 12px beyond it on each side.
const FRAME_CLASS = '-mx-3 rounded-xl border p-3 transition-colors';
const FRAME_READ_CLASS =
  'border-transparent cursor-text hover:bg-bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';
const FRAME_EDIT_CLASS =
  'border-[var(--color-border-secondary)] flex flex-col gap-2';

// A '<' begins a real markup tag only when it is immediately followed by an
// ASCII letter (start tag like <p>/<Entity>), '/' (end tag), or '!'/'?' (a
// comment / declaration) — mirroring the HTML tokenizer. A '<' followed by
// anything else (a literal '<3', a legacy '<@id|Name>' mention, a stray
// comparison) is text the user typed, not a tag, so it must count as content.
function isTagNameStart(ch: string | undefined): boolean {
  return (
    ch !== undefined &&
    ((ch >= 'a' && ch <= 'z') ||
      (ch >= 'A' && ch <= 'Z') ||
      ch === '/' ||
      ch === '!' ||
      ch === '?')
  );
}

// Decide emptiness by scanning characters rather than parsing with DOMParser
// or stripping tags with a regex. DOMParser triggers a CodeQL "DOM text
// reinterpreted as HTML" alert; regex stripping triggers "Incomplete
// multi-character sanitization". A simple character scan avoids both: it
// never calls any DOM API and never removes tags for security purposes.
// Text nodes in PlateNoteEditor output — including mention display names
// inside <Entity>…</Entity> — appear outside tag boundaries (between '>'
// and '<'). We accumulate those visible characters and let String.trim()
// decide emptiness, so Unicode whitespace (NBSP, other space separators)
// is treated as empty exactly as trim() defines it.
//
// Before scanning, run the SAME per-tag guard MarkdownContent applies
// (escapeUnknownRawTags): only real renderable/mention tags stay as `<...>`
// markup to skip; an unknown pseudo-tag like `<string>` is escaped to
// `&lt;string&gt;` (no literal '<'), so the scan keeps it as visible content
// — exactly as MarkdownContent renders it. Without this, `<string>`-only
// content was stripped to empty here while MarkdownContent showed it, so the
// read view wrongly displayed the "Add a description…" placeholder over real
// content.
function isEmpty(s: string): boolean {
  const guarded = escapeUnknownRawTags(s);
  let visibleText = '';
  let inTag = false;
  for (let i = 0; i < guarded.length; i++) {
    const ch = guarded[i];
    if (inTag) {
      if (ch === '>') {
        inTag = false;
      }
      continue;
    }
    if (ch === '<' && isTagNameStart(guarded[i + 1])) {
      inTag = true;
      continue;
    }
    visibleText += ch;
  }
  return visibleText.trim().length === 0;
}

// The read view is a wrapper `role='button'` that opens the editor. Its
// Markdown body can contain its own interactive elements (links, CRM entity
// badges/buttons) whose activation bubbles up here — entering edit mode would
// hijack that navigation. Treat an event as "for a descendant" when it
// originates from an interactive element nested inside the wrapper (not the
// wrapper itself, which legitimately opens the editor).
const INTERACTIVE_SELECTOR =
  'a, button, [role="button"], input, textarea, select';

function isFromInteractiveDescendant(
  target: EventTarget | null,
  wrapper: HTMLElement
): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const interactive = target.closest(INTERACTIVE_SELECTOR);
  return interactive !== null && interactive !== wrapper;
}

export function TaskDescriptionEditor({
  content,
  orgId,
  canEdit,
  onSave,
}: TaskDescriptionEditorProps) {
  const { isEditing, isSaving, error, startEditing, cancelEditing, commit } =
    useInlineFieldEdit<string>(onSave);
  const [draft, setDraft] = useState(content);
  const cancellingRef = useRef(false);

  const enterEdit = useCallback(() => {
    cancellingRef.current = false;
    setDraft(content);
    startEditing();
  }, [content, startEditing]);

  const handleSave = useCallback(async () => {
    // Unchanged content is a no-op: close edit mode without emitting a CRM
    // event (the note update path would otherwise submit a content write with
    // no actual change). Mirrors the other inline editors' skip-if-unchanged.
    if (draft === content) {
      cancelEditing();
      return;
    }
    await commit(draft);
  }, [draft, content, cancelEditing, commit]);

  const handleCancel = useCallback(() => {
    cancellingRef.current = true;
    cancelEditing();
    setDraft(content);
  }, [content, cancelEditing]);

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        // Always intercept Escape so it can't bubble to the enclosing task
        // slideout (which closes on Escape) and hide the editor along with any
        // save-failure feedback.
        e.preventDefault();
        e.stopPropagation();
        // Ignore Escape while a save is in flight — cancelling would discard
        // the draft and drop back to read mode, hiding an error if the
        // request then rejects. Let the save settle first.
        if (isSaving) {
          return;
        }
        handleCancel();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void handleSave();
      }
    },
    [handleCancel, handleSave, isSaving]
  );

  const handleEditRegionBlur = useCallback(
    (e: ReactFocusEvent<HTMLDivElement>) => {
      if (isSaving || cancellingRef.current) {
        return;
      }
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        void handleSave();
      }
    },
    [isSaving, handleSave]
  );

  const handleReadClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (isFromInteractiveDescendant(e.target, e.currentTarget)) {
        return;
      }
      enterEdit();
    },
    [enterEdit]
  );

  const handleReadKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (isFromInteractiveDescendant(e.target, e.currentTarget)) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        enterEdit();
      }
    },
    [enterEdit]
  );

  useEffect(() => {
    if (!isEditing) {
      setDraft(content);
    }
  }, [content, isEditing]);

  if (!canEdit) {
    return (
      <MarkdownContent
        content={content}
        className={READ_CLASS}
        size='compact'
        orgId={orgId}
      />
    );
  }

  if (!isEditing) {
    return (
      <div
        role='button'
        tabIndex={0}
        aria-label='Edit description'
        className={cx(FRAME_CLASS, FRAME_READ_CLASS)}
        onClick={handleReadClick}
        onKeyDown={handleReadKeyDown}
      >
        {isEmpty(content) ? (
          <span className='text-sm text-fg-quaternary'>Add a description…</span>
        ) : (
          <MarkdownContent
            content={content}
            className={READ_CLASS}
            size='compact'
            orgId={orgId}
          />
        )}
      </div>
    );
  }

  // Seamless in-place edit, exactly as the note record slideout behaves: the
  // frame's border appears, the Plate toolbar flows below the body, and the
  // edit AUTOSAVES on blur (⌘/Ctrl+Enter also saves, Esc cancels). No
  // Save/Cancel button row — notes don't have one, and the two must match.
  return (
    <div
      className={cx(FRAME_CLASS, FRAME_EDIT_CLASS)}
      onBlur={handleEditRegionBlur}
    >
      <PlateNoteEditor
        value={draft}
        onChange={setDraft}
        onKeyDown={handleKeyDown}
        variant='inline'
        mentionsEnabled
        richTextEnabled
        readOnly={isSaving}
        autoFocus
        ariaLabel='Description'
        placeholder='Write a description'
        fallback={
          // Read view + toolbar footprint while the lazy Plate chunk resolves,
          // matching MemoForm.tsx's seamless inline editor: without the
          // skeleton the toolbar pops in after the body and shoves the
          // sections below (Attachments, Linked Records) down mid-edit.
          <>
            <MarkdownContent
              content={draft}
              className={READ_CLASS}
              size='compact'
              orgId={orgId}
            />
            <InlineToolbarSkeleton />
          </>
        }
      />
      {error && (
        <p className='text-xs text-utility-error-600'>
          {/* useInlineFieldEdit already runs the error through
              humanizeSaveError — an allowlisted friendly message where the
              backend text matches a known pattern, otherwise the de-scaffolded
              raw text (capped), same as every other inline task field. */}
          {error}
        </p>
      )}
    </div>
  );
}
